import {
  GeoPlacesClient,
  type GeoPlacesClientConfig,
  AutocompleteCommand,
  GetPlaceCommand,
} from "@aws-sdk/client-geo-places";
import { env } from "@/shared/config/env";

/**
 * Thin wrapper around Amazon Location Service (Places) for address autocomplete
 * and place lookup. Replaces the previous direct-to-Nominatim geocoding.
 *
 * Auth uses a scoped, referer-restricted public API key (`v1.public.…`) read
 * from VITE_LOCATION_API_KEY. The key may be absent in environments where it
 * has not been provisioned yet — `isLocationSearchEnabled()` reports that, and
 * callers degrade to plain manual address entry.
 *
 * The API-key auth is inlined (a signer that appends `?key=` to each request)
 * rather than pulled from `@aws/amazon-location-utilities-auth-helper`: that
 * package's entry point re-exports the Cognito helper, which transitively
 * imports the Node-only credential provider chain and breaks the browser build.
 */

export interface AddressSuggestion {
  placeId: string;
  /** Human-readable label to show in the dropdown. */
  label: string;
}

export interface PlaceDetails {
  /** Street line, e.g. "123 Main St". */
  address: string;
  city: string;
  /** Two-letter US state abbreviation, e.g. "TX". */
  state: string;
  zip: string;
  county: string;
  /** Latitude in WGS 84. */
  lat: number;
  /** Longitude in WGS 84. */
  lon: number;
}

export function isLocationSearchEnabled(): boolean {
  return !!env.location.apiKey;
}

let client: GeoPlacesClient | null = null;

function getClient(): GeoPlacesClient {
  const apiKey = env.location.apiKey;
  if (!apiKey) {
    throw new Error(
      "Amazon Location API key is not configured (VITE_LOCATION_API_KEY).",
    );
  }
  if (!client) {
    const config: GeoPlacesClientConfig = {
      region: env.location.region,
      // Custom signer injects the API key as `?key=…`, replacing SigV4 signing.
      signer: {
        sign: async (request) => {
          request.query = { key: apiKey, ...request.query };
          return request;
        },
      },
      // Dummy credentials so the default (Node-only) credential provider chain
      // is never invoked; the signer above does the actual auth.
      credentials: async () => ({ accessKeyId: "", secretAccessKey: "" }),
    };
    client = new GeoPlacesClient(config);
  }
  return client;
}

/**
 * Returns typeahead suggestions for a partial US address. Resolves to an empty
 * array when search is disabled or the query is too short.
 */
export async function autocompleteAddress(
  query: string,
  signal?: AbortSignal,
): Promise<AddressSuggestion[]> {
  const trimmed = query.trim();
  if (!isLocationSearchEnabled() || trimmed.length < 3) return [];

  const resp = await getClient().send(
    new AutocompleteCommand({
      QueryText: trimmed,
      MaxResults: 5,
      Filter: { IncludeCountries: ["USA"] },
    }),
    { abortSignal: signal },
  );

  return (resp.ResultItems ?? [])
    .filter((item) => !!item.PlaceId)
    .map((item) => ({
      placeId: item.PlaceId as string,
      label: item.Title ?? item.Address?.Label ?? "",
    }));
}

/**
 * Resolves a suggestion's PlaceId to its full address components and
 * coordinates. `IntendedUse: "Storage"` is required because we persist the
 * resulting address fields and coordinates on incident/station records.
 */
export async function getPlaceDetails(
  placeId: string,
  signal?: AbortSignal,
): Promise<PlaceDetails> {
  const resp = await getClient().send(
    new GetPlaceCommand({ PlaceId: placeId, IntendedUse: "Storage" }),
    { abortSignal: signal },
  );

  const addr = resp.Address ?? {};
  // Position is [longitude, latitude] in WGS 84.
  const [lon, lat] = resp.Position ?? [];

  if (
    typeof lat !== "number" ||
    typeof lon !== "number" ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lon) ||
    lat < -90 ||
    lat > 90 ||
    lon < -180 ||
    lon > 180
  ) {
    throw new Error("Place has no coordinates");
  }

  const streetLine = [addr.AddressNumber, addr.Street]
    .filter(Boolean)
    .join(" ")
    .trim();

  return {
    address: streetLine,
    city: addr.Locality ?? "",
    state: addr.Region?.Code ?? "",
    zip: addr.PostalCode ?? "",
    county: addr.SubRegion?.Name ?? "",
    lat,
    lon,
  };
}
